import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { LayoutDashboard, PhoneCall, Calendar, BarChart3, LogOut, Building2, Mic, LifeBuoy } from "lucide-react";
import { api } from "../lib/api";
import { setBranding, withAlpha } from "../lib/branding";
import { Toaster } from "./ui/sonner";

const NAV = [
  { to: "", label: "Dashboard", icon: LayoutDashboard, testid: "portal-nav-dashboard", end: true },
  { to: "calls", label: "Calls", icon: PhoneCall, testid: "portal-nav-calls" },
  { to: "appointments", label: "Appointments", icon: Calendar, testid: "portal-nav-appointments" },
  { to: "performance", label: "Performance", icon: BarChart3, testid: "portal-nav-performance" },
  { to: "agent", label: "Your AI", icon: Mic, testid: "portal-nav-agent" },
  { to: "tickets", label: "Support", icon: LifeBuoy, testid: "portal-nav-tickets" },
];

export default function PortalLayout({ children }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const [branding, setLocal] = useState(null);

  // Pull branding fresh on mount (so edits in admin reflect immediately)
  useEffect(() => {
    let alive = true;
    api.get(`/portal/branding/${slug}`).then(({ data }) => {
      if (!alive) return;
      setLocal(data);
      setBranding(data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  const accent = branding?.accent_color || "#FFB000";
  const clientName = branding?.name || "Client";
  const logoUrl = branding?.logo_url;
  const user = JSON.parse(localStorage.getItem("ra_user") || "null");

  const logout = () => {
    localStorage.removeItem("ra_token");
    localStorage.removeItem("ra_user");
    setBranding(null);
    nav(`/portal/${slug}/login`);
  };

  return (
    <div
      className="min-h-screen bg-[#0a0a0b] text-white/95"
      style={{
        // subtle accent radial across the whole portal
        backgroundImage: `radial-gradient(ellipse at top, ${withAlpha(accent, 0.05)}, transparent 60%)`,
      }}
      data-testid="portal-layout"
    >
      <aside
        className="fixed left-0 top-0 h-screen w-64 bg-[#0a0a0b] border-r border-white/5 flex flex-col z-30 hidden md:flex"
        data-testid="portal-sidebar"
      >
        {/* Branded header */}
        <div className="px-6 py-6 flex items-center gap-3 border-b border-white/5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden border"
            style={{
              background: logoUrl ? "transparent" : withAlpha(accent, 0.15),
              borderColor: withAlpha(accent, 0.25),
            }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt={clientName} className="w-full h-full object-contain" />
            ) : (
              <Building2 className="w-5 h-5" style={{ color: accent }} strokeWidth={1.75} />
            )}
          </div>
          <div className="min-w-0">
            <div
              className="font-semibold text-[15px] tracking-tight leading-tight truncate"
              data-testid="portal-client-name"
              title={clientName}
              style={{ color: "#fff" }}
            >
              {clientName}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] mt-1" style={{ color: withAlpha(accent, 0.7) }}>
              AI Front Desk
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to || "root"}
              to={`/portal/${slug}/${to}`.replace(/\/$/, "")}
              end={end}
              data-testid={testid}
              style={({ isActive }) => isActive ? {
                background: withAlpha(accent, 0.12),
                color: accent,
                borderColor: withAlpha(accent, 0.3),
              } : {}}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors border ${
                  isActive
                    ? ""
                    : "text-white/60 hover:text-white hover:bg-white/[0.04] border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.75} />
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/5 px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-9 h-9 rounded-full border flex items-center justify-center text-sm font-semibold"
              style={{
                background: `linear-gradient(135deg, ${withAlpha(accent, 0.3)}, ${withAlpha(accent, 0.05)})`,
                borderColor: withAlpha(accent, 0.25),
                color: accent,
              }}
            >
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm text-white/90 truncate" data-testid="portal-user-name">{user?.name || "User"}</div>
              <div className="text-[11px] text-white/40 truncate font-mono">{user?.email}</div>
            </div>
          </div>
          <button
            data-testid="portal-logout-btn"
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04] border border-white/10 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>

        {/* Tiny "Powered by" — minimal so client feels they own the product */}
        <div className="px-4 pb-3 text-[9px] font-mono uppercase tracking-[0.15em] text-white/20 text-center">
          powered by repliant arc
        </div>
      </aside>

      <main className="md:ml-64 min-h-screen">
        <div className="px-6 md:px-10 py-8 max-w-[1600px]">{children}</div>
      </main>

      <Toaster theme="dark" richColors position="top-right" />
    </div>
  );
}
